import { useState, useCallback, useRef, useEffect } from 'react';
import { getNotificationThreads, getNotificationByNotificationThreadUuid, getGroupMemberCount } from '../services/notification.services';

// Constants
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 5000; // 5 seconds

// TypeScript Interfaces
export interface NotificationThreadData {
    id: string;
    usersGroupId: string;
    title: string;
    messageBody: string;
    rawMessageBody: string;
    lastModified: string;
    channels: string;
    notificationThreadStatus: string;
    emailOnlySubscribers: any[];
    previewedRecipientData?: any;
    isPinned?: boolean;
    authorId?: string;
    authorName?: string;
    authorImageUrl?: string;
    schoolImage?: string;
    groupIntId?: number;
    threadName?: string;
    threadUuid?: string;
    orgUuid?: string;
    selectedUsers?: any[];
    selectedTags?: any[];
    selectedChanel?: string;
    createDate?: string;
    modifiedDate?: string;
    createdBy?: string;
    isAllEmailOnlySubscribersSelected?: boolean;
    isAllSchoolLinkAppUsersSelected?: boolean;
    usertype?: string;
    last_notification_timestamp?: string;
    last_notification_title?: string;
    last_notification_snippet?: string;
    last_notification_status?: string;
    flag?: string;
    [key: string]: any;
}

interface UseNotificationThreadsProps {
    authToken: string;
    onError?: (error: Error) => void;
}

interface UseNotificationThreadsReturn {
    // State
    threads: NotificationThreadData[];
    selectedThread: NotificationThreadData[];
    selectedThreadUUID: string | null;
    activatedNotification: NotificationThreadData | null;
    selectUsersForGroup: { previewedRecipientData: any[]; total: number; memberCountMismatch: boolean };
    isLoading: boolean;
    error: Error | null;
    currentPage: number;
    hasMore: boolean;
    
    // Actions
    loadThreads: (pageNumber: number, searchText: string) => Promise<void>;
    loadMoreThreads: (pageNumber: number, searchText: string) => Promise<void>;
    setActiveThread: (notificationThreadUuid: string, notificationThreadStatus: string, channels: string[], title: string) => Promise<void>;
    createNotification: (data: NotificationThreadData, isCreatingRecipient: boolean) => void;
    updateThread: (threadData: NotificationThreadData) => void;
    removeThread: (threadUUID: string) => void;
    resetSelectedThread: () => void;
    setThreads: React.Dispatch<React.SetStateAction<NotificationThreadData[]>>;
    setSelectedThread: React.Dispatch<React.SetStateAction<NotificationThreadData[]>>;
    setHasMore: React.Dispatch<React.SetStateAction<boolean>>;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    setSelectUsersForGroup: React.Dispatch<React.SetStateAction<{ previewedRecipientData: any[]; total: number; memberCountMismatch: boolean }>>;
}

/**
 * Custom hook for managing notification threads state and operations
 * Includes error handling, retry logic, and request cancellation
 */
export const useNotificationThreads = ({
    authToken,
    onError,
}: UseNotificationThreadsProps): UseNotificationThreadsReturn => {
    // State
    const [threads, setThreads] = useState<NotificationThreadData[]>([]);
    const [selectedThread, setSelectedThread] = useState<NotificationThreadData[]>([]);
    const [selectedThreadUUID, setSelectedThreadUUID] = useState<string | null>(null);
    const [activatedNotification, setActivatedNotification] = useState<NotificationThreadData | null>(null);
    const [selectUsersForGroup, setSelectUsersForGroup] = useState({ previewedRecipientData: [] as any[], total: 0, memberCountMismatch: false });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    // Refs for cleanup and stable references
    const abortControllerRef = useRef<AbortController | null>(null);
    const retryCountRef = useRef(0);
    const threadsRef = useRef<NotificationThreadData[]>([]);
    
    // Keep threadsRef in sync with threads state
    useEffect(() => {
        threadsRef.current = threads;
    }, [threads]);

    // Map thread data to notification format
    const mapThreadData = useCallback((threadData: any[]): NotificationThreadData[] => {
        if (!Array.isArray(threadData) || threadData.length === 0) {
            return [];
        }

        return threadData.map((thread, index) => ({
            id: thread.uuid || `notification_${index}`,
            usersGroupId: thread.uuid,
            name: thread.name, // Actual thread/group name
            title: thread.name || thread.last_notification_title || 'No Title',
            messageBody: thread.last_notification_snippet || 'No messages found',
            rawMessageBody: thread.last_notification_snippet || '',
            lastModified: thread.modifiedDate || thread.last_notification_timestamp || new Date().toISOString(),
            flag: '',
            isPinned: false,
            authorId: thread.createdBy || 'system',
            channels: thread.selectedChanel,
            notificationThreadStatus: thread.status,
            groupIntId: thread.id,
            emailOnlySubscribers: thread.emailOnlySubscribers || [],
            threadName: thread.name,
            threadUuid: thread.uuid,
            orgUuid: thread.orguuid,
            selectedUsers: thread.selectedUsers,
            selectedTags: thread.selectedTags,
            selectedChanel: thread.selectedChanel,
            createDate: thread.createDate,
            modifiedDate: thread.modifiedDate,
            createdBy: thread.createdBy,
            isAllEmailOnlySubscribersSelected: thread.isAllEmailOnlySubscribersSelected,
            isAllSchoolLinkAppUsersSelected: thread.isAllSchoolLinkAppUsersSelected,
            usertype: thread.usertype,
            previewedRecipientData: thread.previewedRecipientData,
            authorName: thread.authorName,
            authorImageUrl: thread.authorImageUrl,
            schoolImage: thread.schoolImage,
            last_notification_timestamp: thread.last_notification_timestamp,
            last_notification_title: thread.last_notification_title,
            last_notification_snippet: thread.last_notification_snippet,
            last_notification_status: thread.last_notification_status,
        }));
    }, []);

    // Load notification threads with retry logic
    const loadThreads = useCallback(async (pageNumber: number, searchText: string) => {
        try {
            setIsLoading(true);
            setError(null);
            
            // Cancel previous request if exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            abortControllerRef.current = new AbortController();

            const response = await getNotificationThreads('', searchText, authToken, pageNumber);
            
            // Check if request was aborted
            if (abortControllerRef.current.signal.aborted) {
                return;
            }

            // Response is already the data array (not wrapped in .data)
            const responseData = Array.isArray(response) ? response : (response as any)?.data || [];
            const mappedThreads = mapThreadData(responseData);
            
            // For initial load (page 0) or search, replace notifications
            setThreads(mappedThreads);
            setHasMore(responseData.length > 0);
            retryCountRef.current = 0; // Reset retry count on success
            
        } catch (err) {
            // Don't handle aborted requests as errors
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }

            const errorObj = err instanceof Error ? err : new Error('Failed to load notification threads');
            setError(errorObj);
            
            if (onError) {
                onError(errorObj);
            }

            // Retry logic
            if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
                retryCountRef.current += 1;
                console.log(`Retrying... Attempt ${retryCountRef.current} of ${MAX_RETRY_ATTEMPTS}`);
                
                setTimeout(() => {
                    loadThreads(pageNumber, searchText);
                }, RETRY_DELAY);
            } else {
                console.error('Max retry attempts reached. Please try again later.');
                retryCountRef.current = 0; // Reset for next operation
            }
        } finally {
            setIsLoading(false);
        }
    }, [authToken, mapThreadData, onError]);

    // Load more threads for pagination
    const loadMoreThreads = useCallback(async (pageNumber: number, searchText: string) => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await getNotificationThreads('', searchText, authToken, pageNumber);
            
            // Response is already the data array (not wrapped in .data)
            const responseData = Array.isArray(response) ? response : (response as any)?.data || [];
            
            setHasMore(responseData.length > 0);
            const mappedThreads = mapThreadData(responseData);
            
            // Append for pagination
            setThreads(prev => [...prev, ...mappedThreads]);
            setCurrentPage(pageNumber);
            
        } catch (err) {
            const errorObj = err instanceof Error ? err : new Error('Failed to load more threads');
            setError(errorObj);
            
            if (onError) {
                onError(errorObj);
            }
        } finally {
            setIsLoading(false);
        }
    }, [authToken, mapThreadData, onError]);

    // Set active notification thread
    const setActiveThread = useCallback(async (
        notificationThreadUuid: string,
        notificationThreadStatus: string,
        channels: string[],
        title: string
    ) => {
        console.log('Setting active thread:', notificationThreadUuid);
        try {
            setIsLoading(true);
            setError(null);
            
            // CRITICAL: Clear everything immediately to prevent showing stale data from previous thread
            setSelectedThreadUUID(notificationThreadUuid);
            setSelectedThread([]); // Clear old messages immediately
            setSelectUsersForGroup({ previewedRecipientData: [], total: 0, memberCountMismatch: false });
            setActivatedNotification(null); // Clear old thread metadata

            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            
            abortControllerRef.current = new AbortController();

            const response = await getNotificationByNotificationThreadUuid(notificationThreadUuid, authToken);

            // Check if request was aborted
            if (abortControllerRef.current.signal.aborted) {
                return;
            }

            const activeNotification = threadsRef.current.find((notification) => notification.usersGroupId === notificationThreadUuid);
            
            // Response is already the data array (not wrapped in .data)
            const responseData = Array.isArray(response) ? response : (response as any)?.data || [];
            // Don't filter out messages - display both ACTIVE and DRAFT status messages
            const finalData = responseData;

            setSelectedThread(finalData);
            
            // Fetch total recipient count (Firestore mobile users + email-only subscribers).
            // Full recipient details are loaded lazily when the modal opens.
            const firestoreMemberCount = await getGroupMemberCount(notificationThreadUuid, authToken);

            if (!abortControllerRef.current.signal.aborted) {
                setSelectUsersForGroup({
                    previewedRecipientData: [],
                    total: firestoreMemberCount || 0,
                    memberCountMismatch: false
                });

                setActivatedNotification(activeNotification ? {
                    ...activeNotification,
                    last_notification_status: activeNotification.last_notification_status
                } : null);
            }

        } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }

            const errorObj = err instanceof Error ? err : new Error('Failed to activate notification thread');
            setError(errorObj);
            
            if (onError) {
                onError(errorObj);
            }
        } finally {
            setIsLoading(false);
        }
    }, [authToken, onError]);

    // Create notification
    const createNotification = useCallback((data: NotificationThreadData, isCreatingRecipient: boolean) => {
        const threadList = [...threadsRef.current];

        if (isCreatingRecipient) {
            threadList.unshift(data);
            setSelectedThread([data]);
            setThreads(threadList);
            setSelectedThreadUUID(data.usersGroupId);
            setActivatedNotification(data);
            
            // Update selectUsersForGroup with the new recipient data
            if (data.previewedRecipientData) {
                try {
                    const recipients = typeof data.previewedRecipientData === 'string' 
                        ? JSON.parse(data.previewedRecipientData) 
                        : data.previewedRecipientData;
                    
                    setSelectUsersForGroup({
                        previewedRecipientData: recipients,
                        total: Array.isArray(recipients) ? recipients.length : 0,
                        memberCountMismatch: false
                    });
                } catch (error) {
                    console.error('Error parsing previewedRecipientData:', error);
                    setSelectUsersForGroup({
                        previewedRecipientData: [],
                        total: 0,
                        memberCountMismatch: false
                    });
                }
            }
        } else {
            const index = threadList.findIndex((n) => n.usersGroupId === data.usersGroupId);

            if (index === -1) {
                console.warn('Notification thread not found for update');
                return;
            }

            const previousData = threadList[index];

            const updatedData = {
                ...data,
                isPinned: previousData?.isPinned || false,
                authorId: previousData?.authorId || data.authorId,
                authorName: previousData?.authorName || data.authorName,
                authorImageUrl: previousData?.authorImageUrl || data.authorImageUrl,
                schoolImage: previousData?.schoolImage || data.schoolImage,
                previewedRecipientData: previousData.previewedRecipientData,
                lastModified: data.lastModified,
                last_notification_title: data.title || previousData.title,
                last_notification_timestamp: data.lastModified,
                last_notification_snippet: data.messageBody,
                last_notification_status: data.last_notification_status || 'DRAFT',
            };

            threadList[index] = updatedData;
            threadList.sort(
                (objA, objB) => new Date(objB.lastModified).getTime() - new Date(objA.lastModified).getTime(),
            );

            setThreads(threadList);
            setSelectedThread(prev => [...prev, updatedData]);
            setSelectedThreadUUID(data.usersGroupId);
        }
    }, []);

    // Update thread
    const updateThread = useCallback((threadData: NotificationThreadData) => {
        setThreads(prev => prev.map(thread => 
            thread.usersGroupId === threadData.usersGroupId ? threadData : thread
        ));
    }, []);

    // Remove thread
    const removeThread = useCallback((threadUUID: string) => {
        setThreads(prev => prev.filter(thread => thread.usersGroupId !== threadUUID));
    }, []);

    // Reset selected thread
    const resetSelectedThread = useCallback(() => {
        setSelectedThreadUUID(null);
        setSelectedThread([]);
        setActivatedNotification(null);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return {
        // State
        threads,
        selectedThread,
        selectedThreadUUID,
        activatedNotification,
        selectUsersForGroup,
        isLoading,
        error,
        currentPage,
        hasMore,
        
        // Actions
        loadThreads,
        loadMoreThreads,
        setActiveThread,
        createNotification,
        updateThread,
        removeThread,
        resetSelectedThread,
        setThreads,
        setSelectedThread,
        setHasMore,
        setCurrentPage,
        setSelectUsersForGroup,
    };
};
